import * as ToastPrimitive from '@radix-ui/react-toast';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { cn } from '../../lib/cn';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

interface ToastRecord {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
}

interface ToastContextValue {
  toast: (input: { title: string; description?: string; tone?: ToastTone }) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const ICONS: Record<ToastTone, ReactNode> = {
  info: <Info className="text-primary size-4" />,
  success: <CheckCircle2 className="text-success size-4" />,
  warning: <AlertTriangle className="text-warning size-4" />,
  danger: <XCircle className="text-danger size-4" />,
};

const ACCENTS: Record<ToastTone, string> = {
  info: 'before:bg-primary',
  success: 'before:bg-success',
  warning: 'before:bg-warning',
  danger: 'before:bg-danger',
};

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastRecord[]>([]);

  const remove = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const value = useMemo<ToastContextValue>(() => {
    const toast: ToastContextValue['toast'] = ({ title, description, tone = 'info' }) => {
      setItems((current) => [...current.slice(-3), { id: nextId++, tone, title, description }]);
    };
    return {
      toast,
      success: (title, description) => toast({ title, description, tone: 'success' }),
      error: (title, description) => toast({ title, description, tone: 'danger' }),
    };
  }, []);

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right" duration={5000}>
        {children}
        {items.map((item) => (
          <ToastPrimitive.Root
            key={item.id}
            onOpenChange={(open) => {
              if (!open) remove(item.id);
            }}
            className={cn(
              'border-line bg-surface-overlay relative flex items-start gap-3 overflow-hidden rounded-md border p-3.5 pl-4 shadow-lg',
              'before:absolute before:inset-y-0 before:left-0 before:w-0.5',
              'data-[state=open]:animate-rise data-[state=closed]:animate-fade-in data-[swipe=end]:animate-fade-in',
              'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
              ACCENTS[item.tone],
            )}
          >
            <span className="mt-0.5 shrink-0">{ICONS[item.tone]}</span>
            <div className="min-w-0 flex-1">
              <ToastPrimitive.Title className="text-sm font-medium">{item.title}</ToastPrimitive.Title>
              {item.description ? (
                <ToastPrimitive.Description className="text-text-subtle mt-0.5 text-xs">
                  {item.description}
                </ToastPrimitive.Description>
              ) : null}
            </div>
            <ToastPrimitive.Close
              className="text-text-faint hover:bg-surface-raised hover:text-text shrink-0 rounded-sm p-1 transition-colors"
              aria-label="Close"
            >
              <X className="size-3.5" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed right-4 bottom-4 z-[60] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider');
  return context;
}
